/**
 * Content for the homepage "Ask your business anything" hero demo.
 *
 * Each scenario is one complete job: who asks, where, what the agent has read, the cited answer and the
 * outcome. Edit the copy here; the animation in useAskDemo reads it and needs no changes. Citation markers
 * in `answer` are written `[n]` and must match the 1-based position of an entry in `sources`.
 */

export type AskChannel = 'web' | 'slack' | 'whatsapp' | 'workspace';

export interface AskScenario {
  id: string;
  /** Short uppercase tag on the scenario tab, e.g. "Support". */
  tag: string;
  /** Tab label after the tag. */
  tabLabel: string;
  avatar: string;
  avatarColor: string;
  askerName: string;
  askerContext: string;
  channel: AskChannel;
  channelLabel: string;
  question: string;
  /** Knowledge the agent holds, shown as chips. */
  knowledge: string[];
  /** Index into `knowledge` of the chip that held the answer. */
  knowledgeHit: number;
  /** File names flicked through while the agent reads. */
  reading: string[];
  found: string;
  answer: string;
  sources: { name: string; location: string }[];
  outcome: string;
}

export interface AskReceipt {
  id: string;
  channel: string;
  meta: string;
  question: string;
  answer: string;
}

export const ASK_SCENARIOS: AskScenario[] = [
  {
    id: 'support',
    tag: 'Support',
    tabLabel: 'Refund on an annual plan',
    avatar: 'V',
    avatarColor: '#c9d4e2',
    askerName: 'Visitor',
    askerContext: 'on yourcompany.com',
    channel: 'web',
    channelLabel: 'Website',
    question: 'Can I get a refund on my annual plan?',
    knowledge: ['yourcompany.com · 212 pages', 'Help center', 'Google Drive · 1,204 files'],
    knowledgeHit: 2,
    reading: [
      'Pricing-FAQ.html',
      'Terms-of-Service.pdf',
      'Refund-Policy-2026.pdf',
      'Billing-Macros.csv',
      'Master-Services-Agreement.pdf',
    ],
    found: 'Found in 2 of 1,204 files',
    answer:
      'Yes. You get a pro-rata refund if you ask within 60 days of your start date [1]. After that, unused months become service credit [2]. Want me to pass this to billing?',
    sources: [
      { name: 'Refund-Policy-2026.pdf', location: 'p. 3 · §4.1' },
      { name: 'Master-Services-Agreement.pdf', location: '§4.2' },
    ],
    outcome: 'Lead captured · handoff to billing offered',
  },
  {
    id: 'finance',
    tag: 'Finance',
    tabLabel: 'Who gets net-60 terms',
    avatar: 'DK',
    avatarColor: '#e9c46a',
    askerName: 'Dana K.',
    askerContext: 'in #finance',
    channel: 'slack',
    channelLabel: 'Slack',
    question: 'Which enterprise clients are approved for net-60 terms?',
    knowledge: ['SharePoint · Finance', 'OneDrive · 486 files'],
    knowledgeHit: 0,
    reading: [
      'AR-Aging-Sept.xlsx',
      'Credit-Policy-2026.pdf',
      'Board-Minutes-Q2.pdf',
      'Exceptions-Q3.xlsx',
      'Vendor-Terms.docx',
    ],
    found: 'Found in 2 of 486 files',
    answer:
      'Four accounts: Northwind, Contoso, Fabrikam and Tailspin [1]. Anyone else needs CFO sign-off under the 2026 credit policy [2].',
    sources: [
      { name: 'Exceptions-Q3.xlsx', location: 'rows 12–15' },
      { name: 'Credit-Policy-2026.pdf', location: '§2.3' },
    ],
    outcome: 'Replied in the #finance thread',
  },
  {
    id: 'schools',
    tag: 'Schools',
    tabLabel: 'Permiso de excursión',
    avatar: 'MR',
    avatarColor: '#9fd3c0',
    askerName: 'Parent',
    askerContext: '· Pre-K family',
    channel: 'whatsapp',
    channelLabel: 'WhatsApp',
    question: '¿Cuándo tengo que entregar el permiso para la excursión de Pre-K?',
    knowledge: ['Parent handbook · ES/EN', 'School website', 'Google Drive · 312 files'],
    knowledgeHit: 0,
    reading: [
      'Calendario-2026.pdf',
      'Parent-Handbook-EN.pdf',
      'Manual-de-Padres-2026.pdf',
      'Field-Trip-Policy.pdf',
      'Portal-de-Familias.html',
    ],
    found: 'Found in 2 of 312 files',
    answer:
      'Cinco días escolares antes de la excursión [1]. El formulario está en el Portal de Familias, en la sección Documentos [2].',
    sources: [
      { name: 'Manual-de-Padres-2026.pdf', location: 'p. 12' },
      { name: 'Portal-de-Familias.html', location: 'Documentos' },
    ],
    outcome: 'Answered in Spanish · 24/7',
  },
  {
    // Public DC PSC record, Formal Case 1176 (checked 2026-09-26 against dcpsc.org's FC1176 case
    // page and Order No. 22328). Written as dated history so it stays true; revisit once the remand
    // is decided (hearings were set for October 2026).
    id: 'energy',
    tag: 'Energy',
    tabLabel: 'A rate order, vacated on appeal',
    avatar: 'AN',
    avatarColor: '#b9c6ff',
    askerName: 'Analyst',
    askerContext: '· research workspace',
    channel: 'workspace',
    channelLabel: 'Team workspace',
    question: 'What happened to the Pepco rate order in Formal Case 1176?',
    knowledge: ['Commission docket · 200,000+ filings', 'Court decisions'],
    knowledgeHit: 0,
    reading: [
      'FC 1176 · Application',
      'Order No. 22328',
      'Order No. 22358',
      'D.C. Court of Appeals opinion',
      'FC 1176 · Order on remand',
    ],
    found: 'Found in 3 records',
    answer:
      "Order No. 22328 (Nov 26, 2024) authorized $123.4M over two years, below Pepco's updated $186.5M request [1]. The D.C. Court of Appeals vacated and remanded it on March 5, 2026 [2]. On May 8, 2026 the Commission kept current rates in effect while the remand proceeds [3].",
    sources: [
      { name: 'Order No. 22328', location: 'Nov 26, 2024' },
      { name: 'D.C. Court of Appeals', location: 'Mar 5, 2026' },
      { name: 'Commission order on rates', location: 'May 8, 2026' },
    ],
    outcome: 'Order → appeal → remand · 3 records cited',
  },
];

/** Earlier answers already sitting in the rail when the page loads. */
export const SEED_RECEIPTS: AskReceipt[] = [
  {
    id: 'seed-teams',
    channel: 'Microsoft Teams',
    meta: 'HR',
    question: 'How many PTO days roll over?',
    answer: 'Up to 5 days carry into Q1 [1]; the rest expire Dec 31 [1].',
  },
  {
    id: 'seed-phone',
    channel: 'Phone line',
    meta: 'Front desk',
    question: 'Are you open on Good Friday?',
    answer: 'Closed Friday; reopening Saturday at 9 am [1].',
  },
  {
    id: 'seed-telegram',
    channel: 'Telegram',
    meta: 'Customer',
    question: 'Do you deliver to Ponce?',
    answer: 'Yes, on Tuesdays and Fridays [1], free over $50 [2].',
  },
];

/** Splits text into plain runs and citation markers, e.g. "a [1]." → ["a ", 1, "."]. */
export function splitCitations(text: string): (string | number)[] {
  return text.split(/\[(\d+)\]/).map((part, i) => (i % 2 === 1 ? Number(part) : part));
}
